import { useCallback, useMemo, useState } from 'react';

import { useWebView } from '@myrealtrip/device-detect';
import { callNativeHandler } from '@myrealtrip/marketing-log';
import compareVersions from 'compare-versions';
import { ChannelsRequestParams, fetchChannels } from 'mrt-apis';
import { getMobileAppVersion } from '@messenger/utils/getMobileAppVersion';

const createAlert = (message: string) => {
  if (typeof window !== 'undefined') window.alert(message);
};

const iOSCloseWithUrlAppliedVersion = '7.64';

export default function useCreateChannel() {
  const { isWebView } = useWebView();
  const [isLoading, setLoading] = useState(false);

  const createChannel = useCallback(
    async ({ gid, partnerId }: ChannelsRequestParams, onError?: () => void) => {
      if (!partnerId) {
        onError?.();
        createAlert('파트너 ID가 존재하지 않아 채팅방 생성에 실패했습니다.');
        return;
      }
      if (!gid) {
        onError?.();
        createAlert('상품 ID가 존재하지 않아 채팅방 생성에 실패했습니다.');
        return;
      }

      setLoading(true);
      try {
        const response = await fetchChannels({ gid, partnerId }, { isWebView })();
        return response?.data?.data?.channelUrl;
      } catch (error: any) {
        if (error?.code === 'common.error.notLogin') {
          return;
        }

        if (['blocked', 'reported'].some((code) => String(error?.code || '').includes(code))) {
          createAlert(error.message);
          return;
        }

        onError?.();
        createAlert('채팅방 생성에 실패했습니다.');
      } finally {
        setLoading(false);
      }

      return;
    },
    [isWebView],
  );

  const createAndOpenChannel = useCallback(
    async (
      { gid, partnerId }: ChannelsRequestParams,
      onError?: () => void,
      isPromotionBridge = false,
    ) => {
      const channelUrl = await createChannel({ gid, partnerId }, onError);

      if (!channelUrl) {
        return;
      }

      const origin = process.env.NEXT_PUBLIC_MRT_ORIGIN || '';
      const channelPageUrl = `${origin}/messenger/channels/${channelUrl}`;
      const url = isWebView
        ? `mrt://chatroom?url=${encodeURIComponent(channelPageUrl)}`
        : channelPageUrl;

      if (isWebView) {
        const [platform, mobileAppVersion] = getMobileAppVersion(window.navigator.userAgent) || [];
        if (platform === 'android') {
          location.href = url;
          if (isPromotionBridge) {
            callNativeHandler('close');
          }
        } else {
          const isCloseWithUrlAppliedVersion =
            mobileAppVersion &&
            compareVersions.compare(mobileAppVersion, iOSCloseWithUrlAppliedVersion, '>=') &&
            isPromotionBridge;
          if (isCloseWithUrlAppliedVersion) {
            callNativeHandler('close', { url });
          } else {
            location.href = url;
          }
        }
      } else {
        location.href = url;
      }
    },
    [createChannel, isWebView],
  );

  return useMemo(
    () => ({
      createChannel,
      createAndOpenChannel,
      isLoading,
    }),
    [createAndOpenChannel, createChannel, isLoading],
  );
}
